import {Artist} from '@app/web-player/artists/artist';
import {Trans} from '@ui/i18n/trans';
import {SmallArtistImage} from '@app/web-player/artists/artist-image/small-artist-image';
import {Link} from 'react-router-dom';
import {getArtistLink} from '@app/web-player/artists/artist-link';
import {TopTracksTable} from '@app/web-player/artists/artist-page/discography-panel/top-tracks-table';
import {
  ArtistAlbumsList,
  ArtistAlbumsSubtypeList,
} from '@app/web-player/artists/artist-page/discography-panel/artist-albums-list';
import {
  ArtistAlbumsGrid,
  ArtistAlbumsSubtypeGrid,
} from '@app/web-player/artists/artist-page/discography-panel/artist-albums-grid';
import {
  ArtistAlbumQueryOptions,
  useArtistAlbums,
} from '@app/web-player/artists/requests/use-artist-albums';
import {useArtistPlaylists} from '@app/web-player/artists/requests/use-artist-playlists';
import {useSettings} from '@ui/settings/use-settings';
import {AdHost} from '@common/admin/ads/ad-host';
import React from 'react';
import {UseArtistResponse} from '@app/web-player/artists/requests/use-artist';
import {albumLayoutKey} from '@app/web-player/artists/requests/use-artist';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {getPageLabel} from '@app/web-player/artists/artist-label';
import {useAlbumTaxonomiesContext} from '@common/admin/album-taxonomies-context';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {getCompatibleSubtypes} from '../../artist-content-type';
import {AlbumSubtype} from '@common/admin/use-album_taxonomies';
import {Button} from '@ui/buttons/button';
import {getRadioLink} from '@app/web-player/radio/get-radio-link';
import {ContentGrid} from '@app/web-player/playable-item/content-grid';
import {PlaylistGridItem} from '@app/web-player/playlists/playlist-grid-item';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';

const PRIMARY_ALBUMS_QUERY: ArtistAlbumQueryOptions = {
  artistCreditRole: 'primary',
  includeDerivedSingles: true,
};

const SECONDARY_COLLABS_QUERY: ArtistAlbumQueryOptions = {
  artistCreditRole: 'secondary',
  includeTrackCollaborations: true,
};

interface DiscographyTabProps {
  data: UseArtistResponse;
}
export function DiscographyTab({
  data: {artist, selectedAlbumLayout},
}: DiscographyTabProps) {
  const {TYPES, SUBTYPES, isLoading} = useAlbumTaxonomiesContext();
  const {player} = useSettings();
  const [viewMode] = useCookie(
    albumLayoutKey,
    selectedAlbumLayout || player?.default_artist_view || 'grid',
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-24 py-20">
        <ProgressCircle aria-label="Loading content..." isIndeterminate />
      </div>
    );
  }

  const compatibleSubtypes = getCompatibleSubtypes(artist, TYPES, SUBTYPES);

  return (
    <div>
      <Header artist={artist} />
      <AdHost slot="artist_bottom" className="mt-34" />
      <div className="mt-44">
        {compatibleSubtypes.map((subtype: AlbumSubtype) => (
          <div key={subtype.slug}>
            {viewMode === 'list' ? (
              <ArtistAlbumsSubtypeList
                artist={artist}
                subtype={subtype}
                initialAlbums={null}
                albumQueryOptions={PRIMARY_ALBUMS_QUERY}
              />
            ) : (
              <ArtistAlbumsSubtypeGrid
                artist={artist}
                subtype={subtype}
                selectedAlbumLayout={selectedAlbumLayout}
                initialAlbums={null}
                albumQueryOptions={PRIMARY_ALBUMS_QUERY}
              />
            )}
          </div>
        ))}

        <AppearsOnSection
          artist={artist}
          viewMode={viewMode as 'list' | 'grid'}
        />
      </div>
    </div>
  );
}

interface AppearsOnSectionProps {
  artist: Artist;
  viewMode: 'list' | 'grid';
}
function AppearsOnSection({artist, viewMode}: AppearsOnSectionProps) {
  const participationsQuery = useArtistAlbums(
    null,
    viewMode,
    artist.id,
    SECONDARY_COLLABS_QUERY,
  );
  const playlistsQuery = useArtistPlaylists(artist.id);
  const hasParticipations = participationsQuery.items.length > 0;
  const hasPlaylists = playlistsQuery.items.length > 0;

  if (!hasParticipations && !hasPlaylists) {
    return null;
  }

  return (
    <section className="mt-40 border-t pt-20">
      <div className="mb-20 flex items-center justify-between gap-12">
        <h2 className="text-base text-muted">
          <Trans message="Appears on" />
        </h2>
        <Button
          elementType={Link}
          to={getRadioLink(artist)}
          variant="outline"
          size="xs"
          radius="rounded-full"
        >
          <Trans message="Go to artist radio" />
        </Button>
      </div>

      {hasParticipations ? (
        <>
          <h3 className="mb-16 text-sm text-muted">
            <Trans message="Participations" />
          </h3>
          {viewMode === 'list' ? (
            <ArtistAlbumsList
              artist={artist}
              initialAlbums={null}
              albumQueryOptions={SECONDARY_COLLABS_QUERY}
              showEmptyMessage={false}
            />
          ) : (
            <ArtistAlbumsGrid
              artist={artist}
              initialAlbums={null}
              albumQueryOptions={SECONDARY_COLLABS_QUERY}
              showEmptyMessage={false}
            />
          )}
        </>
      ) : null}

      {hasPlaylists ? (
      <div className="mt-34">
        <h3 className="mb-16 text-sm text-muted">
          <Trans message="Public playlists" />
        </h3>
        {playlistsQuery.items.length ? (
          <>
            <ContentGrid>
              {playlistsQuery.items.map(playlist => (
                <PlaylistGridItem key={playlist.id} playlist={playlist} />
              ))}
            </ContentGrid>
            <InfiniteScrollSentinel query={playlistsQuery} />
          </>
        ) : null}
      </div>
      ) : null}
    </section>
  );
}

interface HeaderProps {
  artist: Artist;
}
function Header({artist}: HeaderProps) {
  if (!artist.top_tracks?.length) return null;
  const similarArtists = artist.similar?.slice(0, 4) || [];

  return (
    <div className="flex items-start gap-30">
      <TopTracksTable tracks={artist.top_tracks} />
      {similarArtists.length ? (
        <div className="w-1/3 max-w-320 max-md:hidden">
          <h2 className="my-16 text-base text-muted">
            <Trans message={getPageLabel('Similar artists', artist.type)} />
          </h2>
          <div>
            {similarArtists.map(similar => (
              <Link
                key={similar.id}
                to={getArtistLink(similar)}
                className="mb-4 flex cursor-pointer items-center gap-14 rounded p-4 hover:bg-hover"
              >
                <SmallArtistImage
                  artist={similar}
                  className="h-44 w-44 rounded-full object-cover"
                />
                <div className="text-sm">{similar.name}</div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
